import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Separator,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Field,
  SearchableCombobox,
  type SearchableComboboxOption,
  type ToastLike,
} from '@mangovoice/mango-ui'

import type {
  ExtensionOption,
  PhoneNumberOption,
  PlaylistOption,
  SystemSettings,
  SystemSettingsTranslateFn,
  VirtualFaxOption,
} from '../types'
import { text } from '../types'

const DISALLOWED_AREA_CODES = [
  '211',
  '311',
  '411',
  '511',
  '611',
  '711',
  '811',
  '911',
  '555',
  '900',
  '976',
]

function formatPhoneNumber(number: string): string {
  const digits = number.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4)
    const prefix = digits.slice(4, 7)
    const line = digits.slice(7)
    return `(${area}) ${prefix}-${line}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return number
}

// A playlist can be the default hold music if it's a stream (the seeded system
// playlists) or a list with audio tracks. `type` — not `url` — is the stream
// discriminator: the MOH server fills `url` in on list playlists too, so
// `!!url` would wrongly pass an emptied list. When track_count is absent (older
// API), treat it as playable so we never hide every option.
//
// NOTE: same rule lives in extensions-ui `isPlayablePlaylist` and
// music-on-hold-ui `MusicOnHoldListPage`. Keep the three in sync.
const isPlayablePlaylist = (playlist: PlaylistOption): boolean =>
  playlist.type === 'stream' ||
  playlist.track_count == null ||
  playlist.track_count > 0

export interface DefaultsSettingsTabProps {
  settings: SystemSettings
  onSave: (payload: Partial<SystemSettings>) => Promise<void>
  isSaving: boolean
  phoneNumbers?: PhoneNumberOption[]
  faxNumbers?: PhoneNumberOption[]
  virtualFaxExtensions?: VirtualFaxOption[]
  extensions?: ExtensionOption[]
  playlists?: PlaylistOption[]
  isLoadingReferenceData?: boolean
  toast?: ToastLike
  t?: SystemSettingsTranslateFn
}

export const DefaultsSettingsTab = ({
  settings,
  onSave,
  isSaving,
  phoneNumbers = [],
  faxNumbers = [],
  virtualFaxExtensions = [],
  extensions = [],
  playlists = [],
  isLoadingReferenceData,
  toast,
  t,
}: DefaultsSettingsTabProps) => {
  const [areaCode, setAreaCode] = useState(settings.default_area_code)
  const [callerId, setCallerId] = useState(settings.default_caller_id)
  const [faxNumber, setFaxNumber] = useState(settings.default_send_fax_number)
  const [faxExtension, setFaxExtension] = useState<number | null>(
    settings.default_fax_extension,
  )
  const [emergencyName, setEmergencyName] = useState(
    settings.default_emergency_name,
  )
  const [alert911, setAlert911] = useState(settings.alert_911)
  const [holdMusicPlaylist, setHoldMusicPlaylist] = useState<number | null>(
    settings.default_hold_music_playlist,
  )

  useEffect(() => {
    setAreaCode(settings.default_area_code)
    setCallerId(settings.default_caller_id)
    setFaxNumber(settings.default_send_fax_number)
    setFaxExtension(settings.default_fax_extension)
    setEmergencyName(settings.default_emergency_name)
    setAlert911(settings.alert_911)
    setHoldMusicPlaylist(settings.default_hold_music_playlist)
  }, [settings])

  const [areaCodeError, setAreaCodeError] = useState<string | null>(null)

  const validateAreaCode = useCallback(
    (value: string) => {
      const num = parseInt(value, 10)
      if (isNaN(num) || num < 200 || num > 998) {
        setAreaCodeError(
          text(
            t,
            'systemSettings.defaults.areaCodeRange',
            'Area code must be between 200 and 998',
          ),
        )
        return false
      }
      if (DISALLOWED_AREA_CODES.includes(value)) {
        setAreaCodeError(
          text(
            t,
            'systemSettings.defaults.areaCodeDisallowed',
            'This area code is not allowed',
          ),
        )
        return false
      }
      setAreaCodeError(null)
      return true
    },
    [t],
  )

  const virtualFaxOptions = useMemo<SearchableComboboxOption[]>(
    () =>
      virtualFaxExtensions.map((vfx) => ({
        id: String(vfx.id),
        label: `${vfx.name} - ${vfx.number}`,
        // Include both name and number so users can search by either.
        searchText: `${vfx.name} ${vfx.number}`.toLowerCase(),
      })),
    [virtualFaxExtensions],
  )

  const isDirty =
    areaCode !== settings.default_area_code ||
    callerId !== settings.default_caller_id ||
    faxNumber !== settings.default_send_fax_number ||
    faxExtension !== settings.default_fax_extension ||
    emergencyName !== settings.default_emergency_name ||
    alert911 !== settings.alert_911 ||
    holdMusicPlaylist !== settings.default_hold_music_playlist

  const handleSave = useCallback(async () => {
    if (!validateAreaCode(areaCode)) {
      toast?.create({
        title: text(
          t,
          'systemSettings.toast.validationError',
          'Cannot save — please fix the errors above',
        ),
        type: 'error',
        duration: 4000,
      })
      return
    }

    // Only guard when the user actually changed the hold music. A persisted
    // empty default still renders as selected, so enforcing unconditionally
    // would hard-block saves of unrelated fields (area code, caller ID, ...).
    const holdMusicChanged =
      holdMusicPlaylist !== settings.default_hold_music_playlist
    const selectedPlaylist = playlists.find((p) => p.id === holdMusicPlaylist)
    if (
      holdMusicChanged &&
      selectedPlaylist &&
      !isPlayablePlaylist(selectedPlaylist)
    ) {
      toast?.create({
        title: text(
          t,
          'systemSettings.defaults.playlistEmptyError',
          'The selected hold music playlist has no audio tracks. Add tracks or choose a different playlist.',
        ),
        type: 'error',
        duration: 4000,
      })
      return
    }

    await onSave({
      default_area_code: areaCode,
      default_caller_id: callerId,
      default_send_fax_number: faxNumber,
      default_fax_extension: faxExtension,
      default_emergency_name: emergencyName,
      alert_911: alert911,
      default_hold_music_playlist: holdMusicPlaylist,
    })
  }, [
    areaCode,
    callerId,
    faxNumber,
    faxExtension,
    emergencyName,
    alert911,
    holdMusicPlaylist,
    playlists,
    settings.default_hold_music_playlist,
    onSave,
    toast,
    t,
    validateAreaCode,
  ])

  return (
    <VStack gap={8} align="stretch">
      <Box>
        <Heading fontSize="lg" mb={4}>
          {text(t, 'systemSettings.defaults.voiceTitle', 'Voice')}
        </Heading>

        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!areaCodeError} required>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.defaultAreaCode',
                'Default Area Code',
              )}
            </Field.Label>
            <Input
              type="number"
              value={areaCode}
              onChange={(e) => {
                setAreaCode(e.target.value)
                if (areaCodeError) validateAreaCode(e.target.value)
              }}
              min={200}
              max={998}
              maxLength={3}
              bg="bg.subtle"
              borderColor="border.default"
            />
            {areaCodeError && (
              <Field.ErrorText>{areaCodeError}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.defaultCallerId',
                'Default Caller ID',
              )}
            </Field.Label>
            <NativeSelect.Root size="md" disabled={phoneNumbers.length === 0}>
              <NativeSelect.Field
                value={callerId}
                onChange={(e) => setCallerId(e.target.value)}
                bg="bg.subtle"
                borderColor="border.default"
              >
                <option value="">
                  {isLoadingReferenceData
                    ? text(t, 'systemSettings.common.loading', 'Loading...')
                    : text(
                        t,
                        'systemSettings.common.selectOption',
                        '-- Select --',
                      )}
                </option>
                {phoneNumbers.map((pn) => (
                  <option key={pn.id} value={pn.number}>
                    {`${pn.name} - ${formatPhoneNumber(pn.number)}`}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </VStack>
      </Box>

      <Separator />

      <Box>
        <Heading fontSize="lg" mb={4}>
          {text(t, 'systemSettings.defaults.faxTitle', 'Fax')}
        </Heading>

        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.defaultFaxNumber',
                'Default Fax Number',
              )}
            </Field.Label>
            <NativeSelect.Root size="md" disabled={faxNumbers.length === 0}>
              <NativeSelect.Field
                value={faxNumber}
                onChange={(e) => setFaxNumber(e.target.value)}
                bg="bg.subtle"
                borderColor="border.default"
              >
                <option value="">
                  {isLoadingReferenceData
                    ? text(t, 'systemSettings.common.loading', 'Loading...')
                    : text(
                        t,
                        'systemSettings.common.selectOption',
                        '-- Select --',
                      )}
                </option>
                {faxNumbers.map((fn) => (
                  <option key={fn.id} value={fn.number}>
                    {`${fn.name} - ${formatPhoneNumber(fn.number)}`}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.defaultVirtualFax',
                'Default Virtual Fax',
              )}
            </Field.Label>
            {/*
              Larger PBXes have enough virtual-fax extensions that a
              plain native select becomes unusable. Swap for the
              searchable combobox from mango-ui so users can type to
              filter by name or extension number.
            */}
            <SearchableCombobox
              options={virtualFaxOptions}
              value={faxExtension !== null ? String(faxExtension) : ''}
              onChange={(next) => {
                // Guard against a non-numeric id accidentally slipping
                // through — `NaN !== NaN` would flip `isDirty` true
                // forever and make the Save button stick.
                if (!next) {
                  setFaxExtension(null)
                  return
                }
                const parsed = parseInt(next, 10)
                setFaxExtension(Number.isNaN(parsed) ? null : parsed)
              }}
              placeholder={
                isLoadingReferenceData
                  ? text(t, 'systemSettings.common.loading', 'Loading...')
                  : text(
                      t,
                      'systemSettings.defaults.defaultVirtualFaxSearchPlaceholder',
                      'Search virtual fax extensions...',
                    )
              }
              searchAriaLabel={text(
                t,
                'systemSettings.defaults.defaultVirtualFaxSearchAriaLabel',
                'Search virtual fax extensions by name or number',
              )}
              emptyMessage={text(
                t,
                'systemSettings.defaults.defaultVirtualFaxNoMatches',
                'No virtual fax extension matches that search.',
              )}
              noneOptionLabel={text(
                t,
                'systemSettings.common.selectOption',
                '-- Select --',
              )}
              disabled={virtualFaxExtensions.length === 0}
              // Match the background + border of the sibling native
              // selects on this tab so the field blends in visually.
              inputProps={{ bg: 'bg.subtle', borderColor: 'border.default' }}
            />
          </Field.Root>
        </VStack>
      </Box>

      <Separator />

      <Box>
        <Heading fontSize="lg" mb={4}>
          {text(
            t,
            'systemSettings.defaults.emergencyTitle',
            'Emergency Defaults',
          )}
        </Heading>

        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.emergencyName',
                'Emergency Name',
              )}
            </Field.Label>
            <Input
              value={emergencyName}
              onChange={(e) => setEmergencyName(e.target.value)}
              bg="bg.subtle"
              borderColor="border.default"
            />
          </Field.Root>

          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.emergencyNumber',
                'Emergency Number',
              )}
            </Field.Label>
            <Input
              value={settings.default_emergency_number}
              disabled
              bg="bg.muted"
              borderColor="border.default"
            />
            <Field.HelperText>
              {text(
                t,
                'systemSettings.defaults.emergencyNumberHelp',
                'This field is read-only and cannot be changed here.',
              )}
            </Field.HelperText>
          </Field.Root>

          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.emergencyAlert',
                'Emergency Alert',
              )}
            </Field.Label>
            <NativeSelect.Root size="md">
              <NativeSelect.Field
                value={alert911}
                onChange={(e) => setAlert911(e.target.value)}
                bg="bg.subtle"
                borderColor="border.default"
              >
                <option value="">
                  {text(t, 'systemSettings.common.none', 'None')}
                </option>
                {extensions.map((ext) => (
                  <option key={ext.uuid} value={ext.uuid}>
                    {ext.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </VStack>
      </Box>

      <Separator />

      <Box>
        <Heading fontSize="lg" mb={4}>
          {text(t, 'systemSettings.defaults.holdMusicTitle', 'Hold Music')}
        </Heading>

        <VStack gap={4} align="stretch">
          <Field.Root>
            <Field.Label>
              {text(
                t,
                'systemSettings.defaults.holdMusicPlaylist',
                'Hold Music',
              )}
            </Field.Label>
            <NativeSelect.Root size="md" disabled={playlists.length === 0}>
              <NativeSelect.Field
                value={holdMusicPlaylist?.toString() ?? ''}
                onChange={(e) =>
                  setHoldMusicPlaylist(
                    e.target.value ? parseInt(e.target.value, 10) : null,
                  )
                }
                bg="bg.subtle"
                borderColor="border.default"
              >
                <option value="">
                  {isLoadingReferenceData
                    ? text(t, 'systemSettings.common.loading', 'Loading...')
                    : playlists.length === 0
                      ? text(
                          t,
                          'systemSettings.defaults.noPlaylists',
                          'No playlists available',
                        )
                      : text(
                          t,
                          'systemSettings.common.selectOption',
                          '-- Select --',
                        )}
                </option>
                {playlists.map((pl) => {
                  const playable = isPlayablePlaylist(pl)
                  return (
                    <option key={pl.id} value={pl.id} disabled={!playable}>
                      {playable
                        ? pl.name
                        : `${pl.name} ${text(
                            t,
                            'systemSettings.defaults.playlistNotPlayableSuffix',
                            '(no audio tracks)',
                          )}`}
                    </option>
                  )
                })}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        </VStack>
      </Box>

      <HStack gap={3}>
        <Button
          colorPalette="brand"
          onClick={handleSave}
          borderRadius="lg"
          disabled={isSaving || !isDirty}
        >
          {isSaving ? (
            <Spinner size="sm" />
          ) : (
            <Text>{text(t, 'systemSettings.common.save', 'Save Changes')}</Text>
          )}
        </Button>
        {isDirty && (
          <Text fontSize="sm" color="fg.warning" fontWeight="medium">
            {text(t, 'systemSettings.common.unsavedChanges', 'Unsaved changes')}
          </Text>
        )}
      </HStack>
    </VStack>
  )
}
